import axios from "axios";
import { newBackendBaseUrl } from "../config";

// Posts a query to the connect-product GraphQL endpoint and returns the GraphQL
// `data` object (e.g. { problems: [...] }). Throws if the response carries a
// GraphQL-level error so callers can fall back the same way as a failed request.
export const gqlQuery = async (query, variables = {}) => {
  // Auth: send the logged-in user's access token; the backend verifies it via the
  // connect-user backend (same as the other connect-product API calls).
  const token = localStorage.getItem("token");
  const response = await axios.post(
    newBackendBaseUrl + "/connect-product/v1/graphql",
    { query, variables },
    { headers: { "x-access-token": token } }
  );
  if (response.data.errors && response.data.errors.length) {
    throw new Error(response.data.errors[0].message);
  }
  return response.data.data;
};

// GraphQL query that mirrors the legacy /product/get payload (enriched product_color
// rows, with `id` aliased to the parent product id and the row id as product_color_id).
const PRODUCT_COLOR_SEARCH = `query ($filterText: String, $productGroupId: Int, $productIds: [Int!], $offset: Int, $limit: Int) {
  productColorSearch(filterText: $filterText, productGroupId: $productGroupId, productIds: $productIds, offset: $offset, limit: $limit) {
    count
    offset
    next_page
    products {
      id
      product_color_id
      product_id
      color_id
      color_name
      product_name
      product_prefix
      product_group_id
      group_name
      product_image_url
      product_price
      selling_price
      sku_code
      size_id
      size
      warranty_in_months
      extended_warranty
      buffer_date
      launch_date
      hsn_code
      sac_code
      troubleshoot_url
      out_sourced
      unique_id
      new_launch
      status
      buy_back_value
      buy_back_status
      shopify_variant_id
      dv_vol_weight
      dv_act_weight
      dv_exp_vol_weight
      dv_exp_act_weight
      xp_air_vol_weight
      xp_air_act_weight
      bd_vol_weight
      bd_act_weight
      ecom_xp_vol_weight
      ecom_xp_act_weight
      sf_vol_weight
      sf_act_weight
    }
  }
}`;

// Maps a legacy /product/get request body to productColorSearch variables.
const toSearchVars = (data = {}) => {
  const vars = {};
  if (data.filterText) vars.filterText = data.filterText;
  if (data.offset != null) vars.offset = Number(data.offset);
  if (data.limit != null) vars.limit = Number(data.limit);
  const groupId = data.product_group_id != null ? data.product_group_id : data.category_id;
  if (groupId != null && groupId !== "" && Number(groupId) > 0) {
    vars.productGroupId = Number(groupId);
  }
  if (data.product_id != null) {
    vars.productIds = (Array.isArray(data.product_id) ? data.product_id : [data.product_id]).map(Number);
  }
  return vars;
};

// --- product_color upsert mutation (replaces the legacy /product/upsert write) ---
const PRODUCT_COLOR_UPSERT = `mutation ($input: ProductColorInput!) {
  upsertProductColor(input: $input) { success message id }
}`;

const toNum = (v) => (v === "" || v === null || v === undefined ? undefined : Number(v));

// Builds the typed ProductColorInput from the form payload (form values are strings, but the
// schema expects Int/Float), then runs the upsert mutation. Returns { success, message, id }.
// Pass product_color_id to update an existing variant; omit it to insert a new one.
export const upsertProductColor = async (data = {}) => {
  const input = {
    product_id: toNum(data.product_id),
    color_id: toNum(data.color_id),
    size_id: toNum(data.size_id),
    product_image_url: data.product_image_url,
    product_price: toNum(data.product_price),
    selling_price: toNum(data.selling_price),
    troubleshoot_url: data.troubleshoot_url,
    sku_code: data.sku_code,
    hsn_code: data.hsn_code,
    sac_code: data.sac_code,
    warranty_in_months: toNum(data.warranty_in_months),
    extended_warranty: toNum(data.extended_warranty),
    launch_date: data.launch_date || undefined,
    buffer_date: data.buffer_date || undefined,
    buy_back_value: toNum(data.buy_back_value),
    dv_vol_weight: toNum(data.dv_vol_weight),
    dv_act_weight: toNum(data.dv_act_weight),
    dv_exp_vol_weight: toNum(data.dv_exp_vol_weight),
    dv_exp_act_weight: toNum(data.dv_exp_act_weight),
    xp_air_vol_weight: toNum(data.xp_air_vol_weight),
    xp_air_act_weight: toNum(data.xp_air_act_weight),
    bd_vol_weight: toNum(data.bd_vol_weight),
    bd_act_weight: toNum(data.bd_act_weight),
    ecom_xp_vol_weight: toNum(data.ecom_xp_vol_weight),
    ecom_xp_act_weight: toNum(data.ecom_xp_act_weight),
    sf_vol_weight: toNum(data.sf_vol_weight),
    sf_act_weight: toNum(data.sf_act_weight),
  };
  if (data.product_color_id !== undefined && data.product_color_id !== null && data.product_color_id !== "") {
    input.product_color_id = toNum(data.product_color_id);
  }
  // drop omitted fields so we don't overwrite columns with null
  Object.keys(input).forEach((k) => input[k] === undefined && delete input[k]);

  const result = await gqlQuery(PRODUCT_COLOR_UPSERT, { input });
  return result.upsertProductColor; // { success, message, id }
};

// Add one or more product_color variants for a product (replaces /product/add-variant).
// Each variant is upserted by (product_id, color_id) on the backend.
export const addProductVariants = async ({ product_id, variants = [] } = {}) => {
  try {
    const list = Array.isArray(variants) ? variants : [variants];
    for (const v of list) {
      await upsertProductColor({ ...v, product_id });
    }
    return { success: true, message: "Variant(s) added successfully" };
  } catch (e) {
    return { success: false, errors: { message: e.message || "Failed to add variant(s)" } };
  }
};

// Update a single product_color variant (replaces /product/update-variant).
export const updateProductVariant = async ({ product_id, variants } = {}) => {
  try {
    const res = await upsertProductColor({ ...(variants || {}), product_id });
    return { success: !!(res && res.success), message: (res && res.message) || "Updated successfully", id: res && res.id };
  } catch (e) {
    return { success: false, errors: { message: e.message || "Failed to update variant" } };
  }
};

// Calls productColorSearch and returns a legacy-compatible envelope, so consumers that
// read `res.success` and `res.data.{products,count,offset,next_page}` keep working unchanged.
export const productGetCompat = async (data = {}) => {
  const result = await gqlQuery(PRODUCT_COLOR_SEARCH, toSearchVars(data));
  const page = result.productColorSearch;
  return {
    success: true,
    data: {
      count: page.count,
      offset: page.offset,
      next_page: page.next_page,
      prev_page: page.offset > 0,
      products: page.products,
    },
  };
};
